import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Grid, Header, Icon, Message, Segment, SemanticSIZES } from 'semantic-ui-react';
import { Form, Formik } from 'formik';
import { isReady, setSailingProps } from '../../../../app/common/util/util';
import { PermitFormValues, SailingValues } from '../../../../app/models/permit';
import { useStore } from '../../../../app/stores/store';
import StepMenu from '../../steps/StepMenu';
import PermitFormStep2Week1 from './PermitFormStep2Week1';
import PermitFormStep2Week2 from './PermitFormStep2Week2';
import { emptyValidationSchema } from '../../../../app/common/form/FormikFormValidations';
import MyTextArea from '../../../../app/common/form/MyTextArea';

interface Props {
    values: PermitFormValues,
    next: () => void,
    prev: () => void,
    handleChange: (name: string|object, value: string|object) => void,
    goTo: (step: number) => void,
    buttonSize: SemanticSIZES
}

export default observer(function PermitFormStep2({ 
    values,
    next,
    prev,
    handleChange,
    goTo,
    buttonSize
 }: Props) {
    const {permitStore} = useStore();
    const {
        loadRouteScheduleTimes,
        getRouteName
    } = permitStore;
    const [showWeek1MorningError, setShowWeek1MorningError] = useState(false);
    const [showWeek1EveningError, setShowWeek1EveningError] = useState(false);
    const [showWeek2MorningError, setShowWeek2MorningError] = useState(false);
    const [showWeek2EveningError, setShowWeek2EveningError] = useState(false);
    const [msgNum, setMsgNum] = useState(0);

    useEffect( () => {
        loadRouteScheduleTimes(`${values.routeId}`, +values.amTerminalId);
    }, [loadRouteScheduleTimes, values.amTerminalId, values.routeId])

    function handleValidateForUI(values: any) {
        setSailingProps(values);
        
        const week1AmTimes = values.amTimes.filter((x: SailingValues) => x.isSelected);
        const week1PmTimes = values.pmTimes.filter((x: SailingValues) => x.isSelected);

        const week2AmTimes = values.am2Times.filter((x: SailingValues) => x.isSelected);
        const week2PmTimes = values.pm2Times.filter((x: SailingValues) => x.isSelected);
        
        (week1AmTimes.length < 3) ? setShowWeek1MorningError(true) : setShowWeek1MorningError(false);
        (week1PmTimes.length < 3) ? setShowWeek1EveningError(true) : setShowWeek1EveningError(false);

        // week 2
        if(values.scheduleTypeInd === '2') {
            (week2AmTimes.length < 3) ? setShowWeek2MorningError(true) : setShowWeek2MorningError(false);
            (week2PmTimes.length < 3) ? setShowWeek2EveningError(true) : setShowWeek2EveningError(false);
        }
    }

    function handleFormSubmit(values: any) {
        handleValidateForUI(values);

        const ready = isReady(values);
        if(ready){
            next();
        }
    }

    function handleFinalReviewButton() {
        handleValidateForUI(values);
        
        const ready = isReady(values);
        if(ready){
            goTo(6);
        }
    }

    function handleTextArea(value: string) {
        handleChange('customerScheduleComment', value );
        setMsgNum(value.length);
    }

    return (
        <Formik
            initialValues={new PermitFormValues(values)}
            onSubmit={(values) => handleFormSubmit(values)}
            validationSchema={emptyValidationSchema}
        >
        {({handleSubmit}) => (
            <Form className='ui form error' onSubmit={handleSubmit} autoComplete='off'>
                <StepMenu currentTab={values.step} position='top' permitTypeId={values.permitTypeId}/>

                <Segment attached clearing
                    className='step2-container'
                >
                    <Header 
                        as='h2' 
                        icon='calendar' 
                        content={`Weekly Sailing Schedule (Route: ${getRouteName(+values.routeId)})`}
                    />

                    <Grid centered>
                        <Grid.Column width={16}>
                            {(values.permitTypeId === 3 || +values.routeId === 13) && 
                                <Message icon negative>
                                    <Icon name='exclamation triangle' />
                                    <Message.Content>
                                        <Message.Header>Important</Message.Header>
                                        If you are RENEWING for the 6:05 am leaving Southworth or the 4:20 pm leaving Fauntleroy, please choose another sailing time and note your preferred sailing in the comment area below so that we may ensure you remain on your appropriate sailing. Thank you for your cooperation.
                                    </Message.Content>
                                </Message>
                            }

                            {showWeek1MorningError || showWeek1EveningError
                                || showWeek2MorningError || showWeek2EveningError ? (
                            <Message icon color='red'>
                                <Icon name='exclamation' />
                                <Message.Content>
                                    <Message.Header>Requirement</Message.Header>
                                    Please select a minimum of 3 days for Morning Departures and Evening Departures.
                                    <Message.List>
                                        {values.amTimes.filter((x: SailingValues) => x.isSelected).length <= 2 &&
                                        <Message.Item>Selected Morning Departures: {
                                            `${values.amTimes.filter((x: SailingValues) => x.isSelected).length} / 3`
                                        }</Message.Item>
                                        }
                                        {values.pmTimes.filter((x: SailingValues) => x.isSelected).length <= 2 &&
                                        <Message.Item>Selected Evening Departures: {
                                            `${values.pmTimes.filter((x: SailingValues) => x.isSelected).length} / 3`
                                        }</Message.Item>
                                        }
                                        {values.scheduleTypeInd === '2' 
                                            && values.am2Times.filter((x: SailingValues) => x.isSelected).length <= 2
                                            && 
                                            <Message.Item>Selected Evening Departures: {
                                                `${values.am2Times.filter((x: SailingValues) => x.isSelected).length} / 3`
                                            }</Message.Item>
                                        }
                                        {values.scheduleTypeInd === '2' 
                                            && values.pm2Times.filter((x: SailingValues) => x.isSelected).length <= 2
                                            && 
                                            <Message.Item>Selected Evening Departures: {
                                                `${values.pm2Times.filter((x: SailingValues) => x.isSelected).length} / 3`
                                            }</Message.Item>
                                        }
                                    </Message.List>
                                </Message.Content>

                            </Message>
                            ) : (
                            <Message icon color='yellow'>
                                <Icon name='exclamation' />
                                <Message.Content>
                                    <Message.Header>Requirement</Message.Header>
                                    Please select a minimum of 3 days for Morning Departures and Evening Departures.
                                </Message.Content>
                            </Message>
                            )}
                        </Grid.Column>

                        <PermitFormStep2Week1 values={values} morningError={showWeek1MorningError} 
                            eveningError={showWeek1EveningError} handleChange={handleChange} />

                        {values.scheduleTypeInd === "2" &&
                            <PermitFormStep2Week2 values={values} morningError={showWeek2MorningError} 
                                eveningError={showWeek2EveningError} handleChange={handleChange} />
                        }
                        
                        <Grid.Column width={16}>
                            <Segment>
                                <label>If your sailing schedule needs futher description please describe below. Limited to 250 characters:</label>
                                <MyTextArea 
                                    name='customerScheduleComment'
                                    rows={2}
                                    placeholder='Add your comment'
                                    onChange={(value: string) => handleTextArea(value)}
                                />
                                <label>{msgNum} of 200 max characters</label>
                            </Segment>
                        </Grid.Column>
                        
                        <Grid.Column width={16}>
                            {values.isFinalView ? (
                                <Button 
                                    floated='right'
                                    positive
                                    onClick={() => handleFinalReviewButton()}
                                    content='Final Review'
                                    size={buttonSize}
                                /> 
                            ) : (
                                <>
                                    <Button
                                        onClick={() => prev()}
                                        content='Back'
                                        size={buttonSize}
                                    />
                                    <Button
                                        floated='right'
                                        positive
                                        type='submit'
                                        content='Next'
                                        size={buttonSize}
                                    />
                                </>
                            )}
                        </Grid.Column>
                    </Grid>
                </Segment>
            </Form>
            )}
        </Formik>
    )
})